import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Copy, Archive, BarChart3, ExternalLink, Pencil } from "lucide-react";
import api from "@/lib/api";
import { T } from "@/constants/testIds";
import { toast } from "sonner";

export default function FormsList() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/forms");
      setForms(r.data);
    } catch (e) {
      toast.error("Failed to load forms");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const clone = async (id) => {
    try {
      await api.post(`/forms/${id}/clone`);
      toast.success("Form cloned");
      load();
    } catch { toast.error("Clone failed"); }
  };
  const archive = async (id) => {
    if (!window.confirm("Archive this form?")) return;
    await api.post(`/forms/${id}/archive`);
    toast.success("Archived");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Forms</h1>
          <p className="text-sm text-[#A1A1AA] mt-1">Public application forms by role.</p>
        </div>
        <button
          data-testid={T.forms.newBtn}
          onClick={() => navigate("/admin/forms/new")}
          className="tt-btn-primary flex items-center gap-2"
        >
          <Plus size={16} strokeWidth={1.75} /> New form
        </button>
      </div>

      <div className="tt-card overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-[#1E1E1E] tt-label">
          <div className="col-span-4">Title</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-3">Public URL</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {loading && <div className="px-4 py-10 text-center text-[#71717A] font-mono text-xs">LOADING…</div>}
        {!loading && forms.length === 0 && (
          <div className="px-4 py-10 text-center text-[#71717A] text-sm">No forms yet. Create your first.</div>
        )}
        {forms.map((f) => {
          const url = `${window.location.origin}/${f.slug}`;
          return (
            <div
              key={f.form_id}
              data-testid={T.forms.row(f.form_id)}
              className="grid grid-cols-12 items-center px-4 py-4 border-b border-[#1E1E1E] last:border-0 hover:bg-[#121212] transition-colors"
            >
              <div className="col-span-4">
                <div className="font-medium">{f.title}</div>
                <div className="text-xs text-[#71717A] font-mono mt-0.5">{f.form_id}</div>
              </div>
              <div className="col-span-2 text-sm text-[#A1A1AA]">{f.role || "—"}</div>
              <div className="col-span-3">
                <a
                  data-testid={T.forms.publicLink(f.form_id)}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono text-xs text-[#5B9BFF] hover:underline inline-flex items-center gap-1"
                >
                  /{f.slug}
                  <ExternalLink size={10} />
                </a>
              </div>
              <div className="col-span-1">
                {f.archived ? (
                  <span className="tt-badge-neutral">Archived</span>
                ) : (
                  <span className="tt-badge-pass">Live</span>
                )}
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1">
                <button
                  data-testid={T.forms.responsesBtn(f.form_id)}
                  title="Responses"
                  onClick={() => navigate(`/admin/forms/${f.form_id}/responses`)}
                  className="tt-btn-ghost p-1.5"
                >
                  <BarChart3 size={14} strokeWidth={1.75} />
                </button>
                <button
                  data-testid={T.forms.openBtn(f.form_id)}
                  title="Edit"
                  onClick={() => navigate(`/admin/forms/${f.form_id}/edit`)}
                  className="tt-btn-ghost p-1.5"
                >
                  <Pencil size={14} strokeWidth={1.75} />
                </button>
                <button
                  data-testid={T.forms.cloneBtn(f.form_id)}
                  title="Clone"
                  onClick={() => clone(f.form_id)}
                  className="tt-btn-ghost p-1.5"
                >
                  <Copy size={14} strokeWidth={1.75} />
                </button>
                <button
                  data-testid={T.forms.archiveBtn(f.form_id)}
                  title="Archive"
                  onClick={() => archive(f.form_id)}
                  className="tt-btn-ghost p-1.5"
                >
                  <Archive size={14} strokeWidth={1.75} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
